import { Allergen, DietaryRestriction } from '@sg/graphql-schema';

import { type AllergenOrDietaryRestriction } from '../../../utils/dietary-preferences';
import { INITIAL_ALLERGENS, INITIAL_DIETARY_PREFERENCES } from '../setup/context';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Given a list of either allergens or restrictions, map them to only the corresponding allergens.
 */
export function mapToAllergens(allergensOrRestrictions: AllergenOrDietaryRestriction[]) {
  return allergensOrRestrictions.map(mapToAllergen).flat();
}

/**
 * Given a list of either allergens or restrictions, map them to only the corresponding restrictions.
 */
export function mapToRestrictions(allergensOrRestrictions: AllergenOrDietaryRestriction[]) {
  return allergensOrRestrictions.map(mapToRestriction).flat();
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Clean conversion of restrictions to allergens, ignore if already an allergen.
 */
function mapToAllergen(allergenOrRestriction: AllergenOrDietaryRestriction): Allergen[] {
  if (new Set(Object.keys(INITIAL_ALLERGENS)).has(allergenOrRestriction)) {
    return [allergenOrRestriction as Allergen];
  }

  switch (allergenOrRestriction) {
    case DietaryRestriction.Dairy:
      return [Allergen.Milk];
    case DietaryRestriction.Meat:
      return [Allergen.Meat];
    case DietaryRestriction.Nuts:
      return [Allergen.Peanuts, Allergen.TreeNuts];
    case DietaryRestriction.Gluten:
      return [Allergen.Wheat];
    case DietaryRestriction.Fish:
      return [Allergen.Fish];
    case DietaryRestriction.Soy:
      return [Allergen.Soybeans];
    default:
      return [];
  }
}

/**
 * Lossy conversion of allergens to restrictions, ignore if already a restriction.
 * This is needed if the feature flag is disabled, but will lose shellfish, sesame and eggs.
 */
export function mapToRestriction(
  allergenOrRestriction: AllergenOrDietaryRestriction,
): DietaryRestriction[] {
  if (new Set(Object.keys(INITIAL_DIETARY_PREFERENCES)).has(allergenOrRestriction)) {
    return [allergenOrRestriction as DietaryRestriction];
  }

  switch (allergenOrRestriction) {
    case Allergen.Milk:
      return [DietaryRestriction.Dairy];
    case Allergen.Meat:
      return [DietaryRestriction.Meat];
    case Allergen.Peanuts:
      return [DietaryRestriction.Nuts];
    case Allergen.TreeNuts:
      return [DietaryRestriction.Nuts];
    case Allergen.Wheat:
      return [DietaryRestriction.Gluten];
    case Allergen.Fish:
      return [DietaryRestriction.Fish];
    case Allergen.Soybeans:
      return [DietaryRestriction.Soy];
    default:
      return [];
  }
}
